<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard" data-test="connect-jobber-modal">
        <template v-slot:header>Connect Jobber account</template>

        <div class="flex flex-col gap-4">
            <vx-radio-selection v-if="props.locationsAvailable" v-model="scope" data-test="scope">
                <vx-radio-selection-item value="team" :disabled="props.teamConnected" data-test="scope-team">
                    <template v-slot:header>Corporate</template>
                    <template v-slot:content>
                        The corporate Jobber account; its clients belong to no location.
                        <span v-if="props.teamConnected">Already connected.</span>
                    </template>
                </vx-radio-selection-item>
                <vx-radio-selection-item value="location" data-test="scope-location">
                    <template v-slot:header>One location</template>
                    <template v-slot:content
                        >The location's own Jobber account; its clients land in that location.</template
                    >
                </vx-radio-selection-item>
            </vx-radio-selection>

            <div v-if="scope === 'location' && props.locationsAvailable" class="flex flex-col gap-1.5">
                <label :id="locationLabelId" class="text-sm font-bold text-slate-700">Location</label>
                <vx-selectable
                    :aria-labelledby="locationLabelId"
                    placeholder="Search for a location..."
                    :options="locationOptions"
                    label="name"
                    :reduce="(group) => group.group_id"
                    v-model="groupId"
                    :selectable="(group) => !props.connectedGroupIds.includes(group.group_id)"
                    @search="searchGroups"
                    size="md"
                    data-test="location"
                >
                    <template v-slot:option="{ option }">
                        <div class="flex items-center justify-between gap-2 py-1">
                            <div class="flex flex-col">
                                <span class="text-sm font-semibold">{{ option.name }}</span>
                                <span class="text-xs text-slate-500">{{ option.group_id }}</span>
                            </div>
                            <vx-badge
                                v-if="props.connectedGroupIds.includes(option.group_id)"
                                color="success"
                                size="xs"
                            >
                                Connected
                            </vx-badge>
                        </div>
                    </template>
                </vx-selectable>
            </div>

            <div class="text-sm text-slate-500">
                You will be sent to Jobber to sign in to the account and approve the connection, then brought back here.
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex gap-2 justify-end">
                <vx-button type="button" color="muted" size="lg" @click.prevent="visible = false" data-test="cancel">
                    Cancel
                </vx-button>
                <vx-button
                    type="button"
                    size="lg"
                    color="primary"
                    :disabled="!canContinue"
                    @click.prevent="proceed"
                    data-test="continue"
                >
                    Continue to Jobber
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { computed, ref, useId, watch } from 'vue';
import {
    VxBadge,
    VxButton,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
} from '@voxie/frontend-components';
import groupsClient from '~/services/groups/groupsClient';
import debounce from '~/utils/debounce';
import { GROUP_STATUSES_CONFIGURABLE } from '~/constants/groupStatuses';
import { jobberInitiateUrl } from './initiateUrl';

const props = defineProps({
    teamConnected: {
        type: Boolean,
        default: false,
    },
    connectedGroupIds: {
        type: Array,
        default: () => [],
    },
    locationsAvailable: {
        type: Boolean,
        default: false,
    },
    initialScope: {
        type: String,
        default: 'team',
    },
});

const visible = defineModel('visible', { type: Boolean, default: false });

const scope = ref(props.initialScope);
const groupId = ref(null);
const locationOptions = ref([]);
const locationLabelId = useId();

const searchGroups = debounce(async (search, loading) => {
    loading?.(true);

    try {
        const params = { per_page: 15, status: GROUP_STATUSES_CONFIGURABLE };
        if (search) {
            params.query = search;
        }

        const response = await groupsClient.getGroups({ teamId: Spark.state.currentTeam.id }, params);
        locationOptions.value = response.data.data;
    } catch (error) {
        console.error(error);
    } finally {
        loading?.(false);
    }
}, 300);

watch(
    visible,
    (open) => {
        if (!open) {
            return;
        }

        scope.value = props.locationsAvailable ? (props.teamConnected ? 'location' : props.initialScope) : 'team';
        groupId.value = null;

        if (scope.value === 'location') {
            searchGroups('');
        }
    },
    { immediate: true }
);

watch(scope, (value) => {
    if (value === 'location' && locationOptions.value.length === 0) {
        searchGroups('');
    }
});

const canContinue = computed(() => (scope.value === 'team' ? !props.teamConnected : !!groupId.value));

const proceed = () => {
    if (!canContinue.value) {
        return;
    }

    window.location.assign(
        jobberInitiateUrl(Spark.state.currentTeam.id, scope.value === 'location' ? groupId.value : null)
    );
};
</script>
